import {
  Button,
  FormField,
  Grid,
  Multiselect,
  SelectProps,
} from '@amzn/awsui-components-react';
import React, { useCallback, useEffect, useState } from 'react';
import { CloudWatch, CloudwatchWidget } from '../../../Domain/Cloudwatch/CloudWatch';
import { ESEC2 } from '../../../models/types';
import './../../../Styles/cloudwatch-dasboard.css';
import { NVME_INSTANCE_STORE_METRICS_OPTIONS } from './constants';
import { getNVMeInstanceStoreMetrics } from './utils';

const statsOptions: SelectProps.Options = [
  { label: 'Maximum', value: 'Maximum' },
  { label: 'Minimum', value: 'Minimum' },
  { label: 'Average', value: 'Average' },
  { label: 'Sum', value: 'Sum' },
];

interface NvmeForm {
  selectedMetrics: SelectProps.Options;
  instanceIds: SelectProps.Options;
  stats: SelectProps.Options;
  isDirty: boolean;
}

interface NvmeInstanceStoreProps {
  ec2Instances: ESEC2[];
  region: string;
  clientId: string;
  domainName: string;
  rootAccountId: string;
  isOptInRegion: boolean;
  initialValues?: NvmeForm;
}

const NvmeInstanceStore = (props: NvmeInstanceStoreProps) => {
  const instancesIds = props.ec2Instances.map(
    (instance: ESEC2) => instance.instanceId
  );
  const instanceIdOptions = instancesIds.map((instanceId) => ({
    label: instanceId,
    value: instanceId,
  }));

  const initialValues = props.initialValues
    ? props.initialValues
    : {
        selectedMetrics: [],
        instanceIds: [],
        stats: [{ label: 'Average', value: 'Average' }],
        isDirty: false,
      };
  const [formValues, setFormValues] = useState<NvmeForm>(initialValues);
  const [widgets, setWidgets] = useState<CloudwatchWidget[]>([]);

  const isFormInvalid = () => {
    return (
      formValues.stats.length === 0 ||
      formValues.selectedMetrics.length === 0
    );
  };

  const handleSubmit = useCallback(() => {
    setFormValues((prevFormValues: NvmeForm) => ({
      ...prevFormValues,
      isDirty: false,
    }));
    const stats = formValues.stats
      ? formValues.stats.map((stat: SelectProps.Option) =>
          stat ? stat.label : ''
        )
      : ['Average'];

    const metricNames = formValues.selectedMetrics
      ? formValues.selectedMetrics
      : [];
    const selectedInstanceIds = formValues.instanceIds.map(
      (option) => option.label
    );
    const instanceIdsToSend =
      selectedInstanceIds.length > 0 ? selectedInstanceIds : instancesIds;

    const updatedWidgets = getNVMeInstanceStoreMetrics(
      props.domainName,
      props.clientId,
      instanceIdsToSend,
      metricNames,
      stats,
      props.rootAccountId,
      props.region,
      true
    ) as CloudwatchWidget[];
    setWidgets(updatedWidgets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  useEffect(() => {
    if (props.initialValues) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialValues]);

  return (
    <React.Fragment>
      <Grid
        className="dasboard-explorer"
        gridDefinition={[
          { colspan: 3 },
          { colspan: 3 },
          { colspan: 3 },
          { colspan: 1 },
        ]}
      >
        <FormField
          description="Choose NVMe instance store metrics."
          label="Metric"
        >
          <Multiselect
            selectedOptions={formValues.selectedMetrics}
            deselectAriaLabel={(e) => 'Remove ' + e.label}
            options={NVME_INSTANCE_STORE_METRICS_OPTIONS}
            placeholder="Choose options"
            selectedAriaLabel="Selected"
            filteringType="auto"
            onChange={({ detail }) =>
              setFormValues((prevFormValues: NvmeForm) => ({
                ...prevFormValues,
                selectedMetrics: detail.selectedOptions,
                isDirty: true,
              }))
            }
          />
        </FormField>
        <FormField description="Choose stats for the metric." label="Stats">
          <Multiselect
            selectedOptions={formValues.stats}
            deselectAriaLabel={(e) => 'Remove ' + e.label}
            options={statsOptions}
            placeholder="Choose options"
            selectedAriaLabel="Selected"
            onChange={({ detail }) =>
              setFormValues((prevFormValues: NvmeForm) => ({
                ...prevFormValues,
                stats: detail.selectedOptions,
                isDirty: true,
              }))
            }
          />
        </FormField>
        <FormField
          description="Choose Instance IDs to look for metrics."
          label="Instance ID"
        >
          <Multiselect
            disabled={instanceIdOptions.length === 0}
            invalid={formValues.stats.length === 0}
            selectedOptions={formValues.instanceIds}
            deselectAriaLabel={(e) => 'Remove ' + e.label}
            options={instanceIdOptions}
            placeholder="Choose options"
            selectedAriaLabel="Selected"
            filteringType="auto"
            onChange={({ detail }) =>
              setFormValues((prevFormValues: NvmeForm) => ({
                ...prevFormValues,
                instanceIds: detail.selectedOptions,
                isDirty: true,
              }))
            }
          />
        </FormField>
        <Button
          className="explorer-button-checkbox"
          variant="primary"
          disabled={isFormInvalid()}
          onClick={handleSubmit}
        >
          Fetch
        </Button>
      </Grid>
      {widgets && widgets.length > 0 ? (
        <CloudWatch
          region={props.region}
          rootAccountId={props.rootAccountId}
          isOptInRegion={props.isOptInRegion}
          widgets={widgets}
        />
      ) : null}
    </React.Fragment>
  );
};

export { NvmeInstanceStore };
